import { injectable, inject } from 'inversify'
import { makeObservable, action } from 'mobx'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { assertNever } from '@core/lang/assert-never'
import { FeedbackRepository } from './feedback.repository'
import type { FeedbackScope, MessageTone } from './feedback.config'
import type { ActionFailurePm, ActionResponsePm } from './feedback.packing'

export type FeedbackViewModel = {
  showFeedback: boolean
  messages: string[]
  tone: MessageTone
  /** Kept as its own field: "did this fail" is what most callers actually ask. */
  isError: boolean
  dismissLabel: string
}

@injectable()
export class FeedbackPresenter {
  @inject(FeedbackRepository)
  protected messagesRepository!: FeedbackRepository

  @inject(I18nRepository)
  protected i18nRepository!: I18nRepository

  /**
   * Which channel this presenter's own `unpackRepositoryPmToVm` writes to.
   * A getter rather than a field so that a subclass override is in place the
   * moment the base constructor finishes — a subclass *field* initialiser
   * would not be, since it runs after.
   */
  protected get feedbackScope(): FeedbackScope {
    return 'app'
  }

  constructor() {
    makeObservable(this, {
      unpackRepositoryPmToVm: action,
    })
  }

  /**
   * Everything a feedback surface needs for one channel, every branch already
   * resolved (§3.4/§4.3).
   *
   * Takes the scope as an argument instead of reading `feedbackScope`: the
   * channel a presenter *writes* to and the channel a mounted FeedbackComponent
   * *renders* are different questions, and one FeedbackComponent exists per
   * surface (shell, books, ...).
   */
  feedbackVmFor = (scope: FeedbackScope): FeedbackViewModel => {
    const { messages, tone } = this.messagesRepository.channelFor(scope)

    return {
      showFeedback: messages.length > 0,
      dismissLabel: this.i18nRepository.t('common.dismiss'),
      messages,
      tone,
      isError: tone === 'error',
    }
  }

  /**
   * This presenter's own channel — the common case for a feature presenter
   * folding its feedback into its own ViewModel, rather than mounting a
   * separate FeedbackComponent.
   */
  get ownFeedback(): FeedbackViewModel {
    return this.feedbackVmFor(this.feedbackScope)
  }

  /**
   * `scope` defaults to this presenter's own channel, and is passed explicitly
   * when one presenter serves more than one surface — AssetsPagePresenter's list
   * and add-form, for instance. Once a page is split into sub-components with
   * their own sub-presenters, each declares a single `feedbackScope` and the
   * override stops being needed; it is the bridge, not the destination.
   */
  unpackRepositoryPmToVm = (
    pm: ActionResponsePm,
    successMessage: string,
    scope: FeedbackScope = this.feedbackScope,
  ): void => {
    if (!pm.success && pm.failure.code === 'cancelled') return

    this.messagesRepository.set(
      scope,
      pm.success ? [successMessage] : [this.failureMessage(pm.failure)],
      // Derived, never chosen: an action worked or it did not, and no caller
      // gets to disagree with its own result.
      pm.success ? 'success' : 'error',
    )
  }

  /**
   * Application meaning -> localized copy. Mechanism detail never enters.
   *
   * A real method, not an arrow-function class field: a subclass overriding
   * one or two codes and falling through to `super.failureMessage(...)` for
   * the rest needs this on the prototype chain — an instance-level arrow
   * field can't be `super`-called (AssetsPagePresenter is the first real case).
   *
   * `default: assertNever(failure)` makes an unhandled `ActionFailurePm`
   * member a compile error here, independent of tsconfig flags (see
   * assert-never.ts) — this project doesn't set `noImplicitReturns`, so
   * without it a missed case would silently return `undefined` at runtime.
   */
  protected failureMessage(failure: ActionFailurePm): string {
    switch (failure.code) {
      case 'rejected':
        return failure.message
      case 'validation':
        return failure.message
      case 'not-found':
        return failure.message
      case 'conflict':
        return failure.message
      case 'unauthorized':
        return failure.message
      case 'temporarily-unavailable':
        return this.i18nRepository.t('common.failure.temporarilyUnavailable')
      case 'offline':
        return this.i18nRepository.t('common.failure.offline')
      case 'invalid-external-data':
        return this.i18nRepository.t('common.failure.invalidExternalData')
      case 'cancelled':
        return ''
      default:
        return assertNever(failure)
    }
  }

  /**
   * Put a message on a scope's surface directly, with a tone the caller picks.
   *
   * This is the *placement* mechanism without the outcome derivation — for
   * things the app wants to say next to a specific piece of UI that are not the
   * result of a request: "you have unsaved changes", "this list is filtered".
   * They need a scope (so they land in the right place) but there is nothing to
   * derive a tone from, which is exactly why `info` and `warning` are reachable
   * only here.
   *
   * **Never use this to report an action result.** That is what
   * `unpackRepositoryPmToVm` is for, and routing a result through here would
   * hand the caller a free choice about whether its own failure looks like a
   * failure — the one thing the derived path exists to prevent.
   */
  show = action((scope: FeedbackScope, message: string, tone: MessageTone): void => {
    this.messagesRepository.set(scope, [message], tone)
  })

  dismiss = (scope: FeedbackScope): void => {
    this.messagesRepository.reset(scope)
  }
}
